import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND_LOGO } from '../data/homeImages';
import { CATEGORY_LIST } from '../data/catalog';
import { submitLead } from '../utils/leads';
import { SITE, safeHttps } from '../data/site';
import { ArrowRight, Facebook, Instagram, Linkedin, Mail, MapPin, Youtube, Check } from './ui/Icons';

const COLUMNS = [
  {
    title: 'Products',
    links: CATEGORY_LIST.map((c) => ({ label: c.shortName, to: c.route })),
  },
  {
    title: 'Colours & tools',
    links: [
      { label: 'Colour catalogue', to: '/colours/catalogue' },
      { label: 'Interior shades', to: '/InteriorWallPaint/Shades' },
      { label: 'Colour combinations', to: '/colours/combinations' },
      { label: 'Paint calculator', to: '/CalculateNow' },
      { label: 'Technical data sheets', to: '/TDS' },
      { label: 'Wall textures', to: '/textures' },
    ],
  },
  {
    title: 'Services',
    links: [
      { label: 'Safe painting service', to: '/services/safe-painting' },
      { label: 'Waterproofing solutions', to: '/services/waterproofing' },
      { label: 'Colour consultancy', to: '/services/colour-consultancy' },
      { label: 'Book a painter', to: '/services/book-painter' },
      { label: 'Store & dealer locator', to: '/store-locator' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Our story', to: '/AboutUs/OurStory' },
      { label: 'Blog', to: '/blog' },
      { label: 'Painting guides', to: '/guides' },
      { label: 'Videos', to: '/videos' },
      { label: 'Testimonials', to: '/testimonials' },
      { label: 'Contact us', to: '/contact' },
    ],
  },
];

// Links are set in Admin → Site settings; an icon only shows once its link is set.
const socialLinks = () =>
  [
    { label: 'Facebook', href: safeHttps(SITE.social.facebook), Icon: Facebook },
    { label: 'Instagram', href: safeHttps(SITE.social.instagram), Icon: Instagram },
    { label: 'YouTube', href: safeHttps(SITE.social.youtube), Icon: Youtube },
    { label: 'LinkedIn', href: safeHttps(SITE.social.linkedin), Icon: Linkedin },
  ].filter((s): s is { label: string; href: string; Icon: typeof Facebook } => Boolean(s.href));

const Footer = () => {
  const SOCIAL = socialLinks();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  return (
    <footer className="bg-brand-navy text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 -left-32 w-[30rem] h-[30rem] rounded-full bg-brand-blue/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 right-0 w-[28rem] h-[28rem] rounded-full bg-brand-red/15 blur-3xl" />

      {/* Newsletter band */}
      <div className="relative border-b border-white/10">
        <div className="container py-12 lg:py-14 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6">
            <span className="vp-eyebrow vp-eyebrow-light">Stay inspired</span>
            <h2 className="vp-heading !text-white mt-3 text-2xl sm:text-3xl">Colour ideas, new launches and painting tips — once a month.</h2>
          </div>
          <form
            className="lg:col-span-6 flex flex-col sm:flex-row sm:flex-wrap gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!email || sending) return;
              setSending(true);
              setError('');
              const res = await submitLead({
                source: 'newsletter',
                name: 'Newsletter subscriber',
                email,
                message: 'Please add me to the Vertex Paints newsletter.',
              });
              setSending(false);
              if (res.ok) setDone(true);
              else setError(res.error);
            }}
          >
            {done ? (
              <p className="flex items-center gap-2 text-brand-gold font-medium">
                <Check size={16} /> Thanks — you’re on the list.
              </p>
            ) : (
              <>
                <label htmlFor="footer-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="footer-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="vp-input vp-input-dark flex-1"
                />
                <button type="submit" disabled={sending} className="vp-btn vp-btn-gold flex-shrink-0 disabled:opacity-70">
                  {sending ? 'Subscribing…' : 'Subscribe'} <ArrowRight />
                </button>
                {error && (
                  <p role="alert" className="sm:basis-full text-sm text-brand-gold">
                    {error}
                  </p>
                )}
              </>
            )}
          </form>
        </div>
      </div>

      {/* Link columns */}
      <div className="relative container py-14 lg:py-16 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Link to="/" className="inline-block bg-white rounded-xl px-4 py-3" aria-label="Vertex Paints — home">
            <img src={BRAND_LOGO} alt="Vertex Paints" width={1095} height={300} className="h-12 w-auto object-contain" />
          </Link>
          <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-sm">
            Vertex Paints and Coatings Industry — a proudly Indian manufacturer of architectural and industrial coatings for walls, wood and metal, founded in
            2007 and recognised as Best Water-Based Paint Manufacturer at the PaintVision Awards.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            <li>
              <a href={`mailto:${SITE.supportEmail}`} className="inline-flex items-center gap-3 text-white/85 hover:text-brand-gold transition-colors">
                <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Mail size={15} />
                </span>
                {SITE.supportEmail}
              </a>
            </li>
            <li>
              <span className="inline-flex items-start gap-3 text-white/85">
                <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <MapPin size={15} />
                </span>
                <span className="pt-1.5">{SITE.addressLine}</span>
              </span>
            </li>
          </ul>
          {SOCIAL.length > 0 && (
            <div className="mt-7 flex items-center gap-2">
              {SOCIAL.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-brand-gold hover:text-brand-ink text-white flex items-center justify-center transition-colors"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="text-[11px] font-bold tracking-[.16em] uppercase text-white/55 mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.to + l.label}>
                    <Link to={l.to} className="text-[14px] text-white/85 hover:text-brand-gold transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="container py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[13px] text-white/55">
          <p>Copyright © {new Date().getFullYear()} Vertex Paints. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">
              Privacy policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-white transition-colors">
              Terms of use
            </Link>
            <Link to="/sitemap" className="hover:text-white transition-colors">
              Sitemap
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
